/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ModalController, NavController } from "@ionic/angular";
import { firstValueFrom } from "rxjs";
import { ContactWorkflowService } from "src/app/core/services/contact-workflow.service";
import { FacilityService } from "src/app/core/services/pelipost-service-proxies/facility.service";
import { Facility, FacilityJurisdiction } from "src/app/shared/model/facility";
import {
    FacilityLocationCountryModel,
    FacilityLocationStateModel,
} from "src/app/shared/model/facilityLocationModel";
import { FacilitySearchModel } from "src/app/shared/model/facilitySearchModel";
import { BasePage } from "src/app/shared/pages/BasePage";
import { Contact } from "src/app/shared/model/contact";
import { StartupService } from "src/app/core/services/pelipost-service-proxies/startup-service";
import { Browser } from "@capacitor/browser";

@Component({
    selector: "app-facility-search-by-jurisdiction",
    templateUrl: "./facility-search-by-jurisdiction.page.html",
    styleUrls: ["./facility-search-by-jurisdiction.page.scss"]
})
export class FacilitySearchByJurisdictionPage extends BasePage implements OnInit {
    filterForm: UntypedFormGroup=this.formBuilder.group({
            state: [null, Validators.required],
            city: [null]
        });;
    locationData: FacilityLocationCountryModel[] = [];
    states: FacilityLocationStateModel[] = [];
    cities: string[] = [];
    loading = false;
    selectedJurisdiction: number;
    jurisdictionName: string;

    // Facility results
    contact: Contact;
    facilityResults: Facility[] = [];
    searching = false;
    searchAttempted = false;

    FacilityJurisdiction = FacilityJurisdiction;

    get state(): AbstractControl {
        return this.filterForm.get("state");
    }

    get city(): AbstractControl {
        return this.filterForm.get("city");
    }

    get isLocal(): boolean {
        return this.selectedJurisdiction === FacilityJurisdiction.Local;
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private facilityService: FacilityService,
        private contactWorkflowService: ContactWorkflowService,
        private navController: NavController,
        private modalController: ModalController,
        private route: ActivatedRoute,
        private startupService: StartupService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        const params = this.route.snapshot.queryParams;
        this.selectedJurisdiction = params["jurisdiction"] !== undefined ? +params["jurisdiction"] : null;
        this.jurisdictionName = this.getJurisdictionName(this.selectedJurisdiction);

        this.initForm();
        this.contact = await this.contactWorkflowService.GetContactModel();
        await this.loadLocations();
    }

    private getJurisdictionName(jurisdictionId: number): string {
        switch (jurisdictionId) {
            case FacilityJurisdiction.Federal:
                return "Federal";
            case FacilityJurisdiction.State:
                return "State";
            case FacilityJurisdiction.Local:
                return "Local";
            default:
                return "";
        }
    }

    private initForm(): void {
        // Set city requirement based on jurisdiction
        if (this.selectedJurisdiction === FacilityJurisdiction.Local) {
            this.filterForm.get("city").setValidators(Validators.required);
            this.filterForm.get("city").updateValueAndValidity();
        }

        // When state changes, update cities list
        this.filterForm.get("state").valueChanges.subscribe((state: FacilityLocationStateModel) => {
            this.cities = state?.AvailableCities ?? [];
            this.filterForm.get("city").setValue(null);
        });

        // When form becomes valid, search for facilities
        this.filterForm.valueChanges.subscribe(() => {
            this.clearResults();
            if (this.filterForm.valid) {
                this.searchFacilities();
            }
        });
    }

    private async loadLocations(): Promise<void> {
        try {
            this.loading = true;
            this.locationData  = await firstValueFrom(this.facilityService.getFacilityLocations());

            // Default to US states
            if (this.locationData?.length > 0) {
                // Try to find US by name or ID
                const us = this.locationData.find(c =>
                    c.Name === "United States" ||
                    c.Name === "USA" ||
                    c.Id === 1
                );
                this.states = us?.AvailableStates ?? [];
            } else {
                this.states = [];
            }
        } catch (err) {
            await this.showErrorModal(this.modalController, err);
        } finally {
            this.loading = false;
        }
    }

    isFormValid(): boolean {
        return this.filterForm.valid;
    }

    private clearResults(): void {
        this.facilityResults = [];
        this.searchAttempted = false;
    }

    async searchFacilities(): Promise<void> {
        const state = this.filterForm.get("state").value as FacilityLocationStateModel;
        const city = this.filterForm.get("city").value as string;

        const searchModel: FacilitySearchModel = {
            jurisdiction: this.selectedJurisdiction,
            stateProvinceId: state.Id,
            city: this.selectedJurisdiction === FacilityJurisdiction.Local ? city : undefined
        };

        try {
            this.searching = true;
            this.facilityResults = (
                await firstValueFrom(this.facilityService.getFacilities(searchModel))
            ).Data;
        } catch (err) {
            await this.showErrorModal(this.modalController, err);
        } finally {
            this.searching = false;
            this.searchAttempted = true;
        }
    }

    async selectFacility(facility: Facility): Promise<void> {
        this.contact.FacilityId = facility.Id;
        await this.contactWorkflowService.StoreContactModel(this.contact);
        await this.contactWorkflowService.StoreFacility(facility);

        if (facility.RequireInmateId) {
            await this.navController.navigateForward("contacts/new-contact/id");
        } else {
            await this.navController.navigateForward("/contacts/new-contact/additional-info");
        }
    }

    

    async openSupportLink(): Promise<void> {
            const url = (await firstValueFrom(this.startupService.getSettings()))
                .SupportUrl;
            await Browser.open({
                url,
                windowName: "_blank"
            });
        }
}
